/**
 * Portal Explorer Fragment
 * Selection and download for the guest portal's file explorer and its search
 * results page, plus the explorer's typeahead filter.
 */

import { Events } from '../../../../js/utils/events.js';

let initialized = false;
// The folder listing HTMX replaces when the typeahead fires. Stashed on the
// first search so clearing the filter can put it back without a round trip.
let stashedListing = null;

function selectedCount(form) {
    return form.querySelectorAll('input[name="pfile"]:checked, input[name="pfolder"]:checked').length;
}

function refreshDownloadButton(form) {
    const button = form.querySelector('[data-portal-explorer-download]');
    if (!button) return;
    const count = selectedCount(form);
    button.hidden = count === 0;
    const label = button.querySelector('[data-portal-explorer-count]');
    if (label) label.textContent = count > 0 ? ' (' + count + ')' : '';
}

// ============================================================================
// Explorer Handlers
// ============================================================================

const PortalExplorerHandlers = {
    /** Header checkbox: every row follows whatever it was just set to. */
    onSelectAll(event, element) {
        const form = element.form;
        if (!form) return;
        const checked = element.checked;
        form.querySelectorAll('input[name="pfolder"], input[name="pfile"]').forEach((box) => {
            box.checked = checked;
        });
        refreshDownloadButton(form);
    },

    /** A row checkbox changed — the button's count follows the selection. */
    onSelectionChange(event, element) {
        if (element.form) refreshDownloadButton(element.form);
    },

    /**
     * Hand the selection to /drop/portal/download. The button is hidden with
     * an empty selection, so the guard only catches a click that raced a
     * deselection.
     */
    downloadSelected(event, element) {
        const form = element.form;
        if (!form || selectedCount(form) === 0) return;
        const processing = document.querySelector('[data-portal-explorer-processing]');
        if (processing) processing.hidden = false;
        form.submit();
    },

    /**
     * Search results invert each checkbox instead of following the header, so
     * the header reads as "flip the selection" there. Kept as it was.
     */
    onSearchSelectAll(event, element) {
        const form = element.form;
        if (!form) return;
        form.querySelectorAll('input[name="pfile"]').forEach((box) => {
            box.checked = !box.checked;
        });
    },

    /** Search results have no count badge and no empty-selection guard. */
    downloadSearchSelected(event, element) {
        if (element.form) element.form.submit();
    },

    /**
     * Typeahead. An empty filter means the user cleared it: suppress the
     * request and restore the listing we stashed, rather than asking the
     * server for what we already had.
     */
    onFilterRequest(event, element) {
        const body = document.querySelector('[data-portal-explorer-body]');
        if (!body) return;
        const query = (element.value || '').trim();
        if (!query) {
            event.preventDefault();
            if (stashedListing !== null) {
                body.innerHTML = stashedListing;
                if (element.form) refreshDownloadButton(element.form);
            }
            return;
        }
        if (stashedListing === null) stashedListing = body.innerHTML;
        event.detail.parameters.q = query;
    },
};

// ============================================================================
// Initialization
// ============================================================================

function refreshAll() {
    document.querySelectorAll('[data-portal-explorer-form]').forEach(refreshDownloadButton);
}

export function initPortalExplorer() {
    if (initialized) return;
    initialized = true;

    Events.on('change', 'portal-explorer-select-all', PortalExplorerHandlers.onSelectAll);
    Events.on('change', 'portal-explorer-select', PortalExplorerHandlers.onSelectionChange);
    Events.on('click', 'portal-explorer-download', PortalExplorerHandlers.downloadSelected);

    Events.on('change', 'portal-file-select-all', PortalExplorerHandlers.onSearchSelectAll);
    Events.on('click', 'portal-file-download', PortalExplorerHandlers.downloadSearchSelected);

    Events.on('htmx:configRequest', 'portal-explorer-filter', PortalExplorerHandlers.onFilterRequest);

    refreshAll();
    // Searching swaps fresh rows into the listing, carrying whatever was
    // checked before with them.
    document.body.addEventListener('htmx:afterSwap', refreshAll);
}
